import { reactive } from 'vue'
import { applicationLinks } from './architecture-links'

type Row = Record<string, any>
type CollectionMeta = Record<string, { count: number; kind: string }>
type Snapshot = { source: 'database'; model: 'reviewed-v12-candidate'; view: 'current'; sourceKind?: string; snapshotId?: string; scopeId?: string; dataVersion?: string; generatedAt?: string; version?: string; collections: Record<string, any>; collectionMeta?: CollectionMeta }
let snapshot: Snapshot | null = null
let pending: Promise<void> | null = null
let generation = 0
/* 首屏读取使用的基础查询条件（不含 collections）；后续按次请求沿用同一条件。 */
let baseQuery = ''
const typeItemCache = new Map<string, Row[]>()
const itemLookupCache = new WeakMap<Row[], Map<string, Row>>()
export const architecturePackageMeta = reactive({ packageVersion: '正式领域数据库快照（保留导入来源）', snapshotId: '', scopeId: '', issues: { endpoints: 0, legacyReferences: 0, fieldLinks: 0, currentReferences: 0 } })
export const architectureLocators: Row[] = []
const collectionAliases: Record<string,string> = { REQS:'EXTERNAL_REQS',FEATS:'EXTERNAL_FEATS',TCS:'EXTERNAL_CASES',SOLS:'EXTERNAL_SOLUTIONS' }

/** 由当前已加载的集合重建固定版本定位索引（按需合并后调用，保留此前已登记的对象）。 */
function rebuildLocators() {
  architectureLocators.splice(0)
  if (!snapshot) return
  for (const t of snapshot.collections.OBJ_TYPES || []) {
    const raw = snapshot.collections[collectionAliases[t.countSrc] || t.countSrc]
    const values = Array.isArray(raw) ? raw : t.countSrc === 'ABBS' ? Object.values(raw || {}).flat() : []
    for (const item of values as Row[]) if (item.objectId && item.revisionId) architectureLocators.push({
      ...item, typeId: t.code, snapshotId: snapshot.snapshotId, scopeRevisionId: snapshot.scopeId
    })
  }
}
/** 应用一份已校验的数据库快照：写入模块态、页头元信息与对象定位索引。 */
function applySnapshot(data: Snapshot) {
  snapshot = data
  typeItemCache.clear()
  architecturePackageMeta.snapshotId = data.snapshotId || ''
  architecturePackageMeta.scopeId = data.scopeId || ''
  architecturePackageMeta.issues.endpoints = data.collections.UNRESOLVED_RELATIONSHIPS?.length || 0
  architecturePackageMeta.issues.legacyReferences = data.collections.IMPORT_REFERENCE_ISSUES?.length || 0
  architecturePackageMeta.issues.fieldLinks = data.collections.UNRESOLVED_FIELD_LINKS?.length || 0
  architecturePackageMeta.issues.currentReferences = data.collections.CURRENT_REFERENCE_ISSUES?.length || 0
  rebuildLocators()
}
/** 用一次按次请求返回的集合替换本地同名集合（不清掉其他已加载集合）。 */
function applyCollections(data: Snapshot) {
  if (!snapshot) throw new Error('数据库快照尚未加载。')
  Object.assign(snapshot.collections, data.collections)
  snapshot.collectionMeta = { ...snapshot.collectionMeta, ...(data.collectionMeta || {}) }
  if (Array.isArray(data.collections.UNRESOLVED_RELATIONSHIPS)) architecturePackageMeta.issues.endpoints = data.collections.UNRESOLVED_RELATIONSHIPS.length
  if (Array.isArray(data.collections.IMPORT_REFERENCE_ISSUES)) architecturePackageMeta.issues.legacyReferences = data.collections.IMPORT_REFERENCE_ISSUES.length
  if (Array.isArray(data.collections.UNRESOLVED_FIELD_LINKS)) architecturePackageMeta.issues.fieldLinks = data.collections.UNRESOLVED_FIELD_LINKS.length
  if (Array.isArray(data.collections.CURRENT_REFERENCE_ISSUES)) architecturePackageMeta.issues.currentReferences = data.collections.CURRENT_REFERENCE_ISSUES.length
  typeItemCache.clear()
  rebuildLocators()
}

// 总览仅需字典和预聚合统计；领域明细由 ensureCollections 按需读取。
export const OVERVIEW_COLLECTIONS = ['OBJ_TYPES', 'REL_TYPES', 'NAV_BINDINGS', 'BUSINESS_CLASSIFICATIONS', 'CONFIRMED_DATA_DUTIES', 'RELATION_STATS', 'TYPE_STATS', 'NUMBER_PREFIXES', 'C0_FIELDS', 'LIFECYCLE_LABELS', 'AUTHORITY_LABELS', 'ASSERTION_LABELS', 'COVERAGE_LABELS', 'LAYER_LABELS', 'TIER_LABELS', 'REL_COLOR_LEGEND', 'NAV_VIEW_LABELS']

export async function validateArchitecturePackage(query: Record<string, unknown> = {}, options: { reuse?: boolean } = {}) {
  const params = new URLSearchParams()
  const scopeRevisionId = query.scopeRevisionId || query.scopeId
  if (typeof scopeRevisionId === 'string') params.set('scopeRevisionId', scopeRevisionId)
  if (typeof query.snapshotId === 'string') params.set('snapshotId', query.snapshotId)
  for (const key of ['customerCode','scenarioCode']) if (typeof query[key] === 'string') params.set(key, query[key] as string)
  const key = params.toString()
  /* 同一读取上下文（范围/快照/客户/场景）下复用已加载快照：底栏来回切换不再每次重拉 10MB 核心页。
     上下文变化（query 不同）或显式重试（快照为空）时才重新请求。 */
  if (options.reuse && snapshot && baseQuery === key) return
  if (!snapshot && pending) { await pending; if (options.reuse && snapshot && baseQuery === key) return }
  const token = ++generation
  snapshot = null
  typeItemCache.clear()
  architectureLocators.splice(0)
  const request = (async () => {
    /* 首屏只取核心集合；各视角的补充集合在进入该视角时按次向后端请求。 */
    params.set('collections', OVERVIEW_COLLECTIONS.join(','))
    const response = await fetch('/api/arch/v1/page?' + params, { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.timeout(30000) })
    if (!response.ok) throw new Error(`4A 数据库读取失败（HTTP ${response.status}）；未使用 mock 回退。`)
    const body = await response.json()
    const data = body.data as Snapshot
    if (body.code !== 0 || data?.source !== 'database' || data.model !== 'reviewed-v12-candidate' || data.view !== 'current' || !data.collections) throw new Error('4A 数据库响应不符合正式模型契约。')
    const required = ['OBJ_TYPES', 'REL_TYPES', 'NAV_BINDINGS', 'BUSINESS_CLASSIFICATIONS', 'CONFIRMED_DATA_DUTIES', 'RELATION_STATS', 'TYPE_STATS']
    const unavailable = required.filter(key => !Object.prototype.hasOwnProperty.call(data.collections, key))
    if (unavailable.length) throw new Error('v11 模型接入尚未完成，以下页面来源未映射：' + unavailable.join('、') + '。未回退旧快照或 mock。')
    if (!data.collectionMeta || !Object.prototype.hasOwnProperty.call(data.collectionMeta, 'PACKAGE_RELATIONS')) throw new Error('数据库快照未提供按需集合清单（PACKAGE_RELATIONS）。')
    if (token !== generation) throw new Error('架构读取上下文已改变，请重试。')
    applySnapshot(data)
    baseQuery = key
  })()
  pending = request
  try { await request } catch (error) { if (token === generation) pending = null; throw error }
}

/** 请求指定集合并以响应替换本地同名集合；合同内的键必须真实返回（缺失即报错，不回退空集合）。 */
async function fetchCollections(keys: string[], token: number): Promise<void> {
  const params = new URLSearchParams(baseQuery)
  params.set('collections', keys.join(','))
  const response = await fetch('/api/arch/v1/page?' + params, { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.timeout(60000) })
  if (!response.ok) throw new Error(`4A 数据库按需集合读取失败（HTTP ${response.status}）；未使用空集合替代。`)
  const body = await response.json()
  const data = body.data as Snapshot & { missing?: string[] }
  if (body.code !== 0 || data?.source !== 'database' || data.model !== 'reviewed-v12-candidate' || data.view !== 'current' || !data.collections) throw new Error('4A 按需集合响应不符合正式模型契约。')
  if (data.missing?.length) throw new Error('数据库快照缺少按需集合：' + data.missing.join('、') + '；未回退为空集合。')
  if (token !== generation) throw new Error('架构读取上下文已改变，请重试。')
  applyCollections(data)
}

/** 按次请求：进入视角时向后端查询该视角需要的集合。
    后端按集合依赖的来源表判断缓存是否仍有效，未变时直接返回已投影的缓存切片。 */
export async function loadCollections(keys: string[]): Promise<void> {
  const unique = [...new Set(keys.filter(Boolean))]
  if (!unique.length) return
  await ready()
  await fetchCollections(unique, generation)
}

/** 仅补取尚未加载的集合（供按方法取数的场景使用）；已加载的集合不重复请求。 */
export async function ensureCollections(keys: string[]): Promise<void> {
  await ready()
  if (!snapshot) throw new Error('数据库快照尚未加载。')
  const meta = snapshot.collectionMeta
  /* 只按需拉取合同内确实存在的集合；合同外的键（如未入库的外部需求）由调用方按空结果处理。 */
  const missing = [...new Set(keys.filter(Boolean))]
    .filter(key => !Object.prototype.hasOwnProperty.call(snapshot!.collections, key))
    .filter(key => !!meta && Object.prototype.hasOwnProperty.call(meta, key))
  if (!missing.length) return
  await fetchCollections(missing, generation)
}
async function ready(): Promise<void> {
  if (snapshot) return
  if (!pending) await validateArchitecturePackage()
  await pending
}

/* ---------- 集合服务端分页（/api/arch/v1/collection） ----------
   大列表翻页即请求当页切片；投影与缓存仍在服务端，前端不为此下载全量集合。 */
export interface CollectionGroup {
  key: string; count: number; facets?: Record<string, Record<string, number>>
  /* AADATS 按 objRef 分组时服务端下发的组体检（权威登记数 / 未解析引用 / 对象可解析） */
  health?: { owners: number; appsUnresolved: number; objResolved: boolean }
}
export interface CollectionPageResult {
  key: string; kind: 'array' | 'wide'; page: number; pageSize: number; total: number
  rows: any[]; groups?: CollectionGroup[]; version?: string
  h?: (string | null)[]; fieldOrigins?: any[]; unmappedColumns?: any[]; unavailableReason?: string | null; authority?: string
  unresolvedObjectCount?: number
  machine?: { code: string; name: string; definition?: string; objRef?: string | null; applicability?: string; recordStatus?: string; stateCount: number; transitionCount: number }
  /* STATE_MACHINE_DETAIL&sub=transitions 附带的全量状态 码→名 映射（跨页解析 from/to） */
  stateNames?: Record<string, string>
}
export async function fetchCollectionPage(params: Record<string, unknown>): Promise<CollectionPageResult> {
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') search.set(k, String(v))
  const response = await fetch('/api/arch/v1/collection?' + search, { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.timeout(30000) })
  const body = await response.json().catch(() => null)
  if (!response.ok) throw new Error('集合分页读取失败：' + (body?.message || `HTTP ${response.status}`))
  if (body.code !== 0 || !body.data) throw new Error('集合分页响应不符合契约。')
  return body.data as CollectionPageResult
}
export function hasPackageCollection(key: string): boolean {
  return !!snapshot && Object.prototype.hasOwnProperty.call(snapshot.collections, key)
}
/** 集合是否存在于当前快照（已加载），或可按需拉取（见 collectionMeta）。 */
export function hasCollectionMeta(key: string): boolean {
  return hasPackageCollection(key) || (!!snapshot?.collectionMeta && Object.prototype.hasOwnProperty.call(snapshot.collectionMeta, key))
}
/** 集合实例数：优先取已加载数组长度，未加载时使用服务端统计，避免为计数下载大集合。 */
export function collectionCount(key: string): number {
  const value = snapshot?.collections[key]
  if (Array.isArray(value)) return value.length
  if (value && typeof value === 'object') return Object.keys(value).length
  return snapshot?.collectionMeta?.[key]?.count || 0
}
export function packageCollection<T = any>(key: string): T {
  if (!snapshot || !(key in snapshot.collections)) throw new Error(`数据库集合尚未加载：${key}；需先按需拉取，未回退为空集合。`)
  return snapshot.collections[key] as T
}
/** 对象类型对应的集合键（沿用 countSrc 与外部别名规则）。 */
export function sourceKeyOf(type: string): string {
  const meta = snapshot?.collections.OBJ_TYPES?.find((x: Row) => x.code === type)
  return meta ? (collectionAliases[meta.countSrc] || meta.countSrc) : ''
}
export function fixedArchitectureLocator(query: Record<string, unknown>) {
  if (!['objectId', 'revisionId'].some(k => query[k] != null)) return null
  const item = architectureLocators.find(r => r.snapshotId === query.snapshotId && r.objectId === query.objectId && r.revisionId === query.revisionId && (!query.scopeRevisionId || r.scopeRevisionId === query.scopeRevisionId))
  if (!item) throw new Error('指定历史对象不在该数据库快照内；不会替换为最新版。')
  return item
}
const all = (key: string): Row[] => packageCollection(key)
const by = (items: Row[], key = 'code') => Object.fromEntries(items.map(x => [x[key], x]))
const group = (items: Row[], key: string) => items.reduce((a: Record<string, Row[]>, x) => { (a[x[key]] ||= []).push(x); return a }, {})
const pick = (items: Row[], value: string) => items.find(x => [x.code, x.id, x._id, x.objectId, x.s, x.n, x.z].includes(value))
function itemsOf(type: string): Row[] {
  const cached = typeItemCache.get(type)
  if (cached) return cached
  const meta = all('OBJ_TYPES').find(x => x.code === type)
  const source = meta && snapshot!.collections[collectionAliases[meta.countSrc] || meta.countSrc]
  const rows = Array.isArray(source) ? source : meta?.countSrc === 'ABBS' ? Object.values(source || {}).flat() : []
  const items = type === 'VS' ? [...rows, ...all('VALUE_STREAM_STAGES')] : rows
  typeItemCache.set(type, items)
  return items
}
function lookup(items: Row[], ref: string) {
  let index = itemLookupCache.get(items)
  if (!index) {
    index = new Map()
    for (const x of items) for (const k of [x.code, x.id, x._id, x.objectId, x.s, x.n, x.z]) if (k != null && !index.has(k)) index.set(k, x)
    itemLookupCache.set(items, index)
  }
  return index.get(ref)
}
function resolve(endpoint: string) {
  const i = endpoint.indexOf(':'), typeCode = endpoint.slice(0, i), ref = endpoint.slice(i + 1)
  const item = lookup(itemsOf(typeCode), ref)
  return { typeCode, ref, canonical: item?.code || item?.id || item?._id || ref,
    name: item?.name || item?.n || item?.s || item?.z || ref, valid: !!item, item }
}
function relRow(rel: Row) {
  const src = resolve(rel.src), tgt = resolve(rel.tgt)
  if (rel.endpointStatus !== 'resolved') { src.valid = false; tgt.valid = false }
  return { rel, src, tgt }
}
const relations = () => all('PACKAGE_RELATIONS')
const navigationOriginals = new WeakMap<object, Map<string, (...args: any[]) => void>>()
export function installArchitectureNavigationGuard(nav: any, reject: (message: string) => void) {
  const originals = navigationOriginals.get(nav) || new Map<string, (...args: any[]) => void>()
  navigationOriginals.set(nav, originals)
  for (const action of new Set(all('NAV_BINDINGS').filter(b => ['BA','AA','DA','TA'].includes(b.view)).map(b => b.go))) {
    if (typeof action !== 'string' || !action.startsWith('go') || !Object.prototype.hasOwnProperty.call(nav, action) || typeof nav[action] !== 'function') continue
    if (!originals.has(action)) originals.set(action, nav[action])
    nav[action] = (...args: any[]) => {
      const bindings = all('NAV_BINDINGS').filter(b => b.go === action && !b.pending)
      /* 目标类型所属集合尚未按需加载时，先拉取再校验；不能在未加载时把合法对象判为“不在快照内”。 */
      const unloaded = [...new Set(bindings.map(b => sourceKeyOf(b.typeCode)).filter(key => key && !hasPackageCollection(key)))]
      if (unloaded.length) {
        void ensureCollections(unloaded).catch(() => {})
        reject('')
        originals.get(action)!.apply(nav, args)
        return
      }
      if (!bindings.some(b => pick(itemsOf(b.typeCode), args[0]))) {
        reject('目标不在当前数据库快照内，未执行跳转：' + String(args[0] || '空引用'))
        return
      }
      reject('')
      originals.get(action)!.apply(nav, args)
    }
  }
}
function related(type: string, code: string, targetType: string, semantic?: string) {
  const endpoint = `${type}:${code}`
  const found = relations().filter(r => r.endpointStatus === 'resolved' && (!semantic || r.rt === semantic))
    .filter(r => r.src === endpoint || r.tgt === endpoint)
    .map(r => resolve(r.src === endpoint ? r.tgt : r.src))
    .filter(x => x.typeCode === targetType && x.valid).map(x => x.item!)
  return [...new Map(found.map(x => [x.code || x.id || x._id || x.s || x.n, x])).values()]
}
const collectionMethods = (mapping: Record<string, string>) => Object.fromEntries(
  Object.entries(mapping).map(([method, key]) => [method, async () => { await ensureCollections([key]); return packageCollection(key) }])
)
function indexes() {
  const apps = all('APPS'), domains = all('DOMAINS'), grps = all('GRPS')
  return { domains, grps, apps, dgrps: all('DGRPS'), dsubs: all('DSUBS'), dobs: all('DOBJS'), refs: all('REFS'),
    bcs: all('BCS'), rps: all('RPS'), eps: all('EPS'), nds: all('NDS'), scs: all('SCS'),
    appBy: by(apps), domBy: by(domains), grpBy: by(grps), appsByGrp: group(apps, 'grp'), grpsByDom: group(grps, 'dom'),
    stat: [collectionCount('DAENTS'), collectionCount('DAATTS')], exc: [], intg: all('INTG'), abbs: packageCollection('ABBS'), rels: all('RELS') }
}
function node(code: string) {
  const nd = pick(all('NDS'), code); if (!nd) return null
  const apps = related('ND', code, 'APP', 'app_supports_activity')
  return { nd, A: { mgr: [], co: [], missM: [], missC: [], doms: [...new Set(apps.map(x => x.dom))], primary: '' },
    candidateApps: apps, responsibilityStatus: 'unresolved',
    rp: pick(all('RPS'), nd.rp), eps: all('EPS').filter(ep => flowNodes(ep).some(n => n.code === code)),
    ex: exceptions([nd]), caps: related('ND', code, 'CAP').map(x => x.code), note: [] }
}
function flowNodes(ep: Row) {
  return related('EP', ep.code, 'ND')
}
function exceptions(nodes: Row[]) {
  return all('EXCEPTIONS').filter(ex => nodes.some(nd => nd.code === ex.nd)).map(ex => ({
    key: ex.code, nd: nodes.find(nd => nd.code === ex.nd)!, cond: ex.cond,
    A: { mgr: [], co: [], doms: [], primary: '' },
    eps: all('EPS').filter(ep => ep.code === ex.ep), dom: '', responsibilityStatus: 'unresolved'
  }))
}

const archMethods: Record<string, (...args: any[]) => any> = {
  indexes,
  orgApps: (o: Row) => all('APPS').filter(a => (o.domains || []).includes(a.dom) && !(o.lockGrps || []).includes(a.grp)),
  orgLockedGrps: (o: Row) => all('GRPS').filter(g => (o.lockGrps || []).includes(g.code))
    .map(g => ({ g, apps: all('APPS').filter(a => a.grp === g.code), dom: pick(all('DOMAINS'), g.dom) })),
  /* 数据库模式下异常行/节点行暂不回退 mock（避免触发 118MB architecture-collections.json）；后续接入派生接口 */
  exOrgRows: (_o: Row) => [] as Row[],
  ndOrgRows: (_o: Row) => [] as Row[],
  ndStats: node,
  rpStats: (code: string) => {
    const nds = all('NDS').filter(n => n.rp === code)
    return { rp: pick(all('RPS'), code), nds, ex: exceptions(nds), eps: all('EPS').filter(ep => flowNodes(ep).some(n => n.rp === code)) }
  },
  epStats: (code: string) => {
    const ep = pick(all('EPS'), code); if (!ep) return null
    const nds = flowNodes(ep), ex = exceptions(nds)
    return { ep, nds, ex, rps: [...new Set(nds.map(n => n.rp))], byNd: group(ex.map(e => ({ ...e, nodeCode: e.nd.code })), 'nodeCode'), sc: all('SCS').filter(s => s.ep === code) }
  },
  objStats: (code: string) => {
    const o = pick(all('DOBJS'), code); if (!o) return null
    const sub = pick(all('DSUBS'), o.sub), candidateApps = related('DOBJ', code, 'APP', 'app_owns_object')
    const duties = all('CONFIRMED_DATA_DUTIES').filter(d => d.objectCode === code)
    const apps = all('APPS').filter(a => duties.some(d => d.appCode === a.code && d.role === 'responsible' && !d.organizationCode && !d.businessScopeCode && !d.lifecycleStage && !d.scope && !d.exclusions))
    return { o, rels: all('RELS').filter(r => [r.srcCode,r.tgtCode].includes(code)), intg: related('DOBJ', code, 'INTG'),
      app: apps.length === 1 ? apps[0] : null, responsibleApps: apps, candidateApps, duties,
      responsibilityStatus: duties.length ? 'confirmed' : 'unresolved', sub, grp: sub && pick(all('DGRPS'), sub.grp) }
  },
  appModel: (code: string) => {
    const nds = related('APP', code, 'ND', 'app_supports_activity'), integrations = related('APP', code, 'INTG', 'participates')
    const intRole = (role: string) => integrations.filter(i => relations().some(r => r.src === `INTG:${i.code || i._id || i.id}` && r.tgt === `APP:${code}` && r.attrs?.role === role))
    return { a: pick(all('APPS'), code), mgr: [], co: [], candidateNodes: nds, responsibilityStatus: 'unresolved', int: { asMgr: intRole('provider'), asUse: intRole('consumer') },
      indirectCaps: pick(all('APPS'), code)?.indirectCapabilities || [], caps: related('APP', code, 'CAP'), eps: related('APP', code, 'EP'), objs: related('APP', code, 'DOBJ'),
      abb: packageCollection<Record<string, Row[]>>('ABBS')[code] || [] }
  },
  domStats: (code: string) => {
    const apps = all('APPS').filter(a => a.dom === code), blocks = apps.flatMap(a => packageCollection<Record<string, Row[]>>('ABBS')[a.code] || [])
    const unique = (rows: Row[]) => [...new Map(rows.map(x => [x.code || x._id || x.id, x])).values()]
    const nds = unique(apps.flatMap(a => related('APP', a.code, 'ND')))
    return { apps, grps: all('GRPS').filter(g => g.dom === code), objs: unique(apps.flatMap(a => related('APP', a.code, 'DOBJ'))), nds,
      intg: unique(apps.flatMap(a => related('APP', a.code, 'INTG'))), ex: exceptions(nds),
      abb: blocks.length, sub: blocks.reduce((n, b) => n + (b.subs?.length || 0), 0) }
  },
  appLinks: (code: string) => applicationLinks(code, { reqs: snapshot?.collections.EXTERNAL_REQS, feats: snapshot?.collections.EXTERNAL_FEATS, cases: snapshot?.collections.EXTERNAL_CASES, sols: snapshot?.collections.EXTERNAL_SOLUTIONS,
    services: all('AASVCS') as Array<{code:string;appCode:string}>,
    interfaces: all('AALIFS') as Array<{code:string;name:string;svcRef:string;style:string}>,
    interfaceLabels: Object.fromEntries(all('BUSINESS_CLASSIFICATIONS').filter(r=>r.family==='INTERFACE_STYLE').map(r=>[r.code,r.label])) })
}
/* 方法级按需集合：视图点击时只拉取该方法真正读取的集合；动态类型按来源集合追加。
   DETAIL_* 宽表不再经快照内存读取，统一走 /api/arch/v1/collection 服务端分页（fetchCollectionPage）。 */
const ARCH_DEPENDENCIES: Record<string, string[]> = {
  indexes: ['APPS','DOMAINS','GRPS','DGRPS','DSUBS','DOBJS','REFS','BCS','RPS','EPS','NDS','SCS','INTG','ABBS','RELS'], orgApps: ['APPS'], orgLockedGrps: ['APPS','GRPS','DOMAINS'], exOrgRows: [], ndOrgRows: [],
  /* 关联解析会遍历任意端点，VS 端点还需要阶段集合，否则详情会因缺少 VALUE_STREAM_STAGES 整体失败。 */
  ndStats: ['PACKAGE_RELATIONS','VALUE_STREAM_STAGES','NDS','APPS','RPS','EPS','EXCEPTIONS','BCS'],
  rpStats: ['PACKAGE_RELATIONS','VALUE_STREAM_STAGES','RPS','NDS','EPS','EXCEPTIONS','BCS'],
  epStats: ['PACKAGE_RELATIONS','VALUE_STREAM_STAGES','EPS','NDS','EXCEPTIONS','RPS','BCS','SCS'],
  objStats: ['PACKAGE_RELATIONS','VALUE_STREAM_STAGES','DOBJS','DSUBS','DGRPS','RELS','INTG','APPS','CONFIRMED_DATA_DUTIES'],
  appModel: ['PACKAGE_RELATIONS','VALUE_STREAM_STAGES','APPS','NDS','INTG','BCS','EPS','DOBJS','ABBS'],
  domStats: ['PACKAGE_RELATIONS','VALUE_STREAM_STAGES','APPS','GRPS','ABBS','NDS','DOBJS','INTG','EXCEPTIONS'],
  appLinks: ['AASVCS','AALIFS','BUSINESS_CLASSIFICATIONS']
}
const MODEL_DEPENDENCIES: Record<string, string[]> = {
  modelMeta: [], typeStats: [], relStats: [],
  /* 关系端点解析可命中任意类型；VS 的 itemsOf 还会合并阶段集合，须一并取齐再解析。 */
  relInstances: ['PACKAGE_RELATIONS'], endpointsOf: ['PACKAGE_RELATIONS','VALUE_STREAM_STAGES'],
  typeSamples: [], objectDetail: ['PACKAGE_RELATIONS','VALUE_STREAM_STAGES'], resolveNav: [],
  /* 架构关系页签的关系图配置：总览首屏集合不携带，进入页签时按次拉取。 */
  diagram: ['REL_DIAGRAM_NODES','REL_DIAGRAM_EDGES','REL_DIAGRAM_RELS','REL_DIAGRAM_IMPACT','REL_DIAGRAM_LAYERS','REL_DIAGRAM_STATUS']
}
function methodDependencies(name: string, args: any[], map: Record<string, string[]>): string[] {
  if (name === 'typeSamples' || name === 'objectDetail' || name === 'resolveNav') {
    const source = sourceKeyOf(args[0])
    const extra = args[0] === 'VS' ? ['VALUE_STREAM_STAGES'] : []
    return [...new Set([...(map[name] || []), ...(source ? [source] : []), ...extra])]
  }
  return map[name] || []
}
function asyncMethods(methods: Record<string, (...args: any[]) => any>, map: Record<string, string[]>) {
  return Object.fromEntries(Object.entries(methods).map(([key, fn]) => [key, async (...args: any[]) => {
    await ready()
    const deps = methodDependencies(key, args, map)
    if (deps.length) await ensureCollections(deps)
    return fn(...args)
  }]))
}
export const databaseArchRepo: any = {
  ...asyncMethods(archMethods, ARCH_DEPENDENCIES),
  ...collectionMethods({ concepts: 'CONCEPTS', conceptDoms: 'CONCEPT_DOMS', appServices: 'AASVCS', appLogicIfs: 'AALIFS', appDataDuties: 'AADATS', logicEntities: 'DAENTS', dataAttributes: 'DAATTS' }),
  ...collectionMethods({ phyTables: 'PHY_TABLES' }),
  fullNeed: () => !!snapshot, fullReady: () => !!snapshot, fullState: () => snapshot ? 'ready' : 'error',
  fullBar: () => snapshot ? '' : '数据库详情尚未加载'
}
export const databaseBizRepo: any = collectionMethods({ valueStreams: 'VALUE_STREAMS', bizRules: 'BIZ_RULES', bizVals: 'BIZ_VALS', stateMachines: 'STATE_MACHINES', bizEvents: 'BIZ_EVENTS', goals: 'GOALS', bizDomains: 'BIZ_DOMAINS', bizRoles: 'BIZ_ROLES' })
export const databaseTechRepo: any = collectionMethods({ netLayers: 'NET_LAYERS', netZones: 'NET_ZONES', envs: 'ENVS', deploySvcs: 'DEPLOY_SVCS', swLayers: 'SW_LAYERS' })
export const databaseModelRepo: any = asyncMethods({
  modelMeta: () => ({ objTypes: all('OBJ_TYPES'), relTypes: all('REL_TYPES'), navBindings: all('NAV_BINDINGS'), numberPrefixes: all('NUMBER_PREFIXES'), c0Fields: all('C0_FIELDS'),
    labels: { lifecycle: packageCollection('LIFECYCLE_LABELS'), authority: packageCollection('AUTHORITY_LABELS'), assertion: packageCollection('ASSERTION_LABELS'),
      coverage: packageCollection('COVERAGE_LABELS'), layer: packageCollection('LAYER_LABELS'), tier: packageCollection('TIER_LABELS') },
    colorLegend: all('REL_COLOR_LEGEND'), navViewLabels: packageCollection('NAV_VIEW_LABELS') }),
  /* 关系页签的关系图配置（节点 / 连线 / 关系目录 / 影响说明 / 分层 / 状态）。 */
  diagram: () => ({
    nodes: packageCollection('REL_DIAGRAM_NODES'), edges: packageCollection('REL_DIAGRAM_EDGES'),
    rels: packageCollection('REL_DIAGRAM_RELS'), impact: packageCollection('REL_DIAGRAM_IMPACT'),
    layers: packageCollection('REL_DIAGRAM_LAYERS'), status: packageCollection('REL_DIAGRAM_STATUS')
  }),
  /* 概览统计使用服务端预聚合，避免为计数下载 PACKAGE_RELATIONS / DAATTS；集合已加载时口径相同。 */
  typeStats: () => snapshot?.collections.TYPE_STATS || Object.fromEntries(all('OBJ_TYPES').map(t => [t.code, itemsOf(t.code).length])),
  relInstances: relations,
  relStats: () => snapshot?.collections.RELATION_STATS || (() => {
    const counts = (key: string) => relations().reduce((a: Row, r) => { a[r[key]] = (a[r[key]] || 0) + 1; return a }, {})
    const broken: Row = {}
    for (const r of relations()) for (const ep of [r.src, r.tgt]) if (!resolve(ep).valid) broken[ep] = (broken[ep] || 0) + 1
    return { total: relations().length, byType: counts('rt'), byAm: counts('am'), byCs: counts('cs'), broken: Object.entries(broken).map(([endpoint, count]) => ({ endpoint, count })) }
  })(),
  endpointsOf: (type: string) => ({ rt: all('REL_TYPES').find(r => r.code === type), rows: relations().filter(r => r.rt === type).map(relRow) }),
  typeSamples: (type: string, limit = 8) => itemsOf(type).slice(0, limit).map(x => ({ ref: x.code || x.id || x._id || x.s || x.n, name: x.name || x.n || x.s })),
  objectDetail: (type: string, ref: string) => {
    const end = resolve(type + ':' + ref), endpoint = type + ':' + end.canonical, item = end.item || null
    return { type: all('OBJ_TYPES').find(t => t.code === type), item, end,
      apiFields: type === 'API' && item ? all('API_FIELDS').filter(f => f.apiCode === item.code) : null,
      c0: item ? all('C0_FIELDS').filter(f => item[f.key] != null).map(field => ({ field, value: item[field.key] })) : [],
      outRels: relations().filter(r => r.src === endpoint).map(relRow), inRels: relations().filter(r => r.tgt === endpoint).map(relRow) }
  },
  resolveNav: (type: string, ref: string) => {
    const binding = all('NAV_BINDINGS').find(b => b.typeCode === type), end = resolve(type + ':' + ref)
    return { binding, code: end.canonical, view: binding?.view || '', go: binding?.go || '', pending: !end.valid || (binding?.pending ?? true), label: binding?.label || '无可用跳转' }
  }
}, MODEL_DEPENDENCIES)
